/* ─── Starburst shape with softened tips + mood face ─── */
export const softStar = (cx, cy, outerR, innerR, points) => {
  const parts = [];
  const total = points * 2;
  const roundness = 0.35;

  for (let i = 0; i < total; i++) {
    const angle = (Math.PI * 2 * i) / total - Math.PI / 2;
    const nextAngle = (Math.PI * 2 * (i + 1)) / total - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    const nextR = (i + 1) % 2 === 0 ? outerR : innerR;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    const nx = cx + nextR * Math.cos(nextAngle);
    const ny = cy + nextR * Math.sin(nextAngle);

    if (i === 0) parts.push(`M${x},${y}`);

    const midAngle = (angle + nextAngle) / 2;
    const midR = (r + nextR) / 2 + (outerR - innerR) * roundness;
    const cpx = cx + midR * Math.cos(midAngle);
    const cpy = cy + midR * Math.sin(midAngle);

    parts.push(`Q${cpx},${cpy} ${nx},${ny}`);
  }
  parts.push('Z');
  return parts.join(' ');
};

export const MOOD_COLORS = {
  okay:       { from: '#a8e6cf', to: '#88d8b0', shadow: '#5cb889' },
  thoughtful: { from: '#c4b5fd', to: '#a78bfa', shadow: '#7c5ce0' },
  strong:     { from: '#fcd34d', to: '#fbbf24', shadow: '#d99a06' },
  heavy:      { from: '#c4c4c4', to: '#a3a3a3', shadow: '#787878' },
  grateful:   { from: '#f9a8d4', to: '#f472b6', shadow: '#d44a94' },
  emotional:  { from: '#c4b5fd', to: '#a78bfa', shadow: '#8266d4' },
};

export const MOOD_FACES = {
  okay:       { eyes: 'happy',   mouth: 'smile' },
  thoughtful: { eyes: 'look',    mouth: 'flat'  },
  strong:     { eyes: 'happy',   mouth: 'grin'  },
  heavy:      { eyes: 'sad',     mouth: 'frown' },
  grateful:   { eyes: 'closed',  mouth: 'smile' },
  emotional:  { eyes: 'sad',     mouth: 'wavy'  },
};

const MoodFace = ({ mood, size = 160 }) => {
  const face = MOOD_FACES[mood] || MOOD_FACES.okay;
  const colors = MOOD_COLORS[mood] || MOOD_COLORS.okay;
  const fc = '#2d1040';
  const gradId = `blob-${mood}-${size}`;
  const noiseId = `noise-${mood}-${size}`;
  const shadowId = `shadow-${mood}-${size}`;
  const clipId = `clip-${mood}-${size}`;

  // Scale factor relative to the 160px viewBox
  const vb = 160;
  const outerR = 68 * (size < 40 ? 0.9 : 1); // slightly tighter at tiny sizes
  const innerR = 50 * (size < 40 ? 0.9 : 1);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${vb} ${vb}`} fill="none">
      <defs>
        <linearGradient id={gradId} x1="40" y1="20" x2="120" y2="140" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={colors.from} />
          <stop offset="100%" stopColor={colors.to} />
        </linearGradient>
        <filter id={noiseId} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" result="noise" />
          <feColorMatrix type="saturate" values="0" in="noise" result="grayNoise" />
          <feBlend in="SourceGraphic" in2="grayNoise" mode="overlay" />
        </filter>
        <filter id={shadowId} x="-10%" y="-5%" width="120%" height="125%">
          <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor={colors.shadow} floodOpacity="0.25" />
        </filter>
        <clipPath id={clipId}>
          <path d={softStar(80, 80, outerR, innerR, 10)} />
        </clipPath>
      </defs>
      <path d={softStar(80, 80, outerR, innerR, 10)} fill={`url(#${gradId})`} filter={`url(#${shadowId})`} />
      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="0" width={vb} height={vb} fill={`url(#${gradId})`} filter={`url(#${noiseId})`} opacity="0.45" />
      </g>
      {/* Eyes */}
      {face.eyes === 'happy' && (
        <>
          <ellipse cx="64" cy="74" rx="4.5" ry="5" fill={fc} />
          <ellipse cx="96" cy="74" rx="4.5" ry="5" fill={fc} />
        </>
      )}
      {face.eyes === 'look' && (
        <>
          <ellipse cx="66" cy="74" rx="4.5" ry="5" fill={fc} />
          <ellipse cx="98" cy="74" rx="4.5" ry="5" fill={fc} />
          <ellipse cx="68" cy="73" rx="1.8" ry="1.8" fill="#fff" />
          <ellipse cx="100" cy="73" rx="1.8" ry="1.8" fill="#fff" />
        </>
      )}
      {face.eyes === 'sad' && (
        <>
          <ellipse cx="64" cy="76" rx="4.5" ry="4" fill={fc} />
          <ellipse cx="96" cy="76" rx="4.5" ry="4" fill={fc} />
          <path d="M56 68 Q64 64, 72 68" stroke={fc} strokeWidth="2.5" strokeLinecap="round" fill="none" />
          <path d="M88 68 Q96 64, 104 68" stroke={fc} strokeWidth="2.5" strokeLinecap="round" fill="none" />
        </>
      )}
      {face.eyes === 'closed' && (
        <>
          <path d="M56 74 Q64 68, 72 74" stroke={fc} strokeWidth="3" strokeLinecap="round" fill="none" />
          <path d="M88 74 Q96 68, 104 74" stroke={fc} strokeWidth="3" strokeLinecap="round" fill="none" />
        </>
      )}
      {/* Mouth */}
      {face.mouth === 'smile' && (
        <path d="M66 92 Q80 104, 94 92" stroke={fc} strokeWidth="3" strokeLinecap="round" fill="none" />
      )}
      {face.mouth === 'grin' && (
        <path d="M64 90 Q80 106, 96 90" stroke={fc} strokeWidth="3" strokeLinecap="round" fill="none" />
      )}
      {face.mouth === 'flat' && (
        <path d="M68 94 L92 94" stroke={fc} strokeWidth="3" strokeLinecap="round" />
      )}
      {face.mouth === 'frown' && (
        <path d="M66 98 Q80 88, 94 98" stroke={fc} strokeWidth="3" strokeLinecap="round" fill="none" />
      )}
      {face.mouth === 'wavy' && (
        <path d="M64 94 Q74 100, 80 94 Q86 88, 96 94" stroke={fc} strokeWidth="3" strokeLinecap="round" fill="none" />
      )}
    </svg>
  );
};

export default MoodFace;
